/**
 * API Client for Frontend
 * Handles HTTP requests with authentication, retries, and error handling
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { Auth, onAuthStateChanged } from 'firebase/auth';
import { firebaseAuth } from '../firebaseConfig';
import { logger } from './logger';

// /api/* reaches the functions (netlify.toml; the desktop app forwards it to the site). There is
// no function called "api": /.netlify/functions/api/... was answered with the app's own page,
// which the bell read as no notifications, so it never showed one.
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

// Get Firebase auth instance
const auth: Auth = firebaseAuth;

// Wait for Firebase to restore auth state from local persistence before sending requests.
// Without this, requests fire before currentUser is available → 401 errors.
const authReady: Promise<void> = new Promise((resolve) => {
  let unsub: (() => void) | undefined;
  unsub = onAuthStateChanged(auth, () => {
    if (unsub) unsub();
    resolve();
  });
});

interface APIError {
  status: number;
  message: string;
  data?: any;
}

class APIClient {
  private client: AxiosInstance;
  private refreshingToken: Promise<string> | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor - add auth token (waits for Firebase auth init)
    this.client.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        try {
          // Wait for Firebase to restore auth state before checking currentUser
          await authReady;
          const user = auth.currentUser;
          if (user) {
            const token = await this.getAuthToken();
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch (error) {
          logger.error('Failed to get auth token', error);
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - handle errors and retries
    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        const config = error.config as InternalAxiosRequestConfig & { _retry?: number };
        if (!config) return Promise.reject(error);

        // Handle token expiration
        if (error.response?.status === 401 && !config._retry) {
          config._retry = 1;
          try {
            const token = await this.refreshAuthToken();
            config.headers.Authorization = `Bearer ${token}`;
            return this.client(config);
          } catch (refreshError) {
            logger.error('Token refresh failed', refreshError);
            // Optionally redirect to login
            return Promise.reject(refreshError);
          }
        }

        // Retry on network errors or 5xx errors
        if (this.shouldRetry(error) && (config._retry || 0) < MAX_RETRIES) {
          config._retry = (config._retry || 0) + 1;
          await this.delay(RETRY_DELAY * config._retry);
          logger.debug('Retrying request', {
            url: config.url,
            attempt: config._retry,
          });
          return this.client(config);
        }

        return Promise.reject(this.normalizeError(error));
      }
    );
  }

  private async getAuthToken(): Promise<string> {
    const user = auth.currentUser;
    if (!user) throw new Error('No authenticated user');
    
    // Get token, forcing refresh if close to expiring
    const tokenResult = await user.getIdTokenResult();
    const expirationTime = new Date(tokenResult.expirationTime).getTime();
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000;

    if (expirationTime - now < fiveMinutes) {
      return this.refreshAuthToken();
    }

    return tokenResult.token;
  }

  private async refreshAuthToken(): Promise<string> {
    if (this.refreshingToken) {
      return this.refreshingToken;
    }

    this.refreshingToken = (async () => {
      try {
        const user = auth.currentUser;
        if (!user) throw new Error('No authenticated user');
        logger.debug('Refreshing auth token');
        return await user.getIdToken(true);
      } finally {
        this.refreshingToken = null;
      }
    })();

    return this.refreshingToken;
  }

  private shouldRetry(error: AxiosError): boolean {
    if (!error.config) return false;
    if (error.code === 'ECONNABORTED') return true; // Timeout
    if (error.code === 'ENOTFOUND') return true; // DNS error
    // Don't retry connection-refused or generic network errors — server is down
    if (!error.response) return false;
    return error.response.status >= 500; // Server errors
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private normalizeError(error: AxiosError): APIError {
    if (error.response) {
      return {
        status: error.response.status,
        message: (error.response.data as any)?.message || error.message,
        data: error.response.data,
      };
    }
    return {
      status: 0,
      message: error.message || 'Network error',
      data: null,
    };
  }

  // Public API methods
  public async get<T>(url: string, config = {}): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  public async post<T>(url: string, data?: any, config = {}): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  public async put<T>(url: string, data?: any, config = {}): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  public async delete<T>(url: string, config = {}): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }

  public async patch<T>(url: string, data?: any, config = {}): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }
}

// Export singleton instance
export const apiClient = new APIClient();

// Export types
export type { APIError };
